import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ConsentProvider } from "@/context/ConsentContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsentBanner from "@/components/ConsentBanner";
import Home from "@/pages/Home";
import Standorte from "@/pages/Standorte";
import StandortDetail from "@/pages/StandortDetail";
import Spielerschutz from "@/pages/Spielerschutz";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import NotFound from "@/pages/NotFound";
import { Toaster } from "@/components/ui/sonner";

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminLocations = lazy(() => import("@/pages/admin/AdminLocations"));
const AdminLocationForm = lazy(() => import("@/pages/admin/AdminLocationForm"));
const AdminPromotions = lazy(() => import("@/pages/admin/AdminPromotions"));
const AdminAudit = lazy(() => import("@/pages/admin/AdminAudit"));

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Entfernt das serverseitige Prerender-Overlay: bei statischen Seiten sofort,
// bei dynamischen Seiten sobald die Seite __swnPageReady meldet. Bei API-Fehlern
// (__swnPageError) bleibt der Server-Inhalt sichtbar und erhaelt einen Hinweis.
function PrerenderDismiss() {
  useEffect(() => {
    const overlay = document.getElementById("prerender-overlay");
    if (!overlay) return;
    const path = window.location.pathname;
    const isDynamic = path === "/" || path === "/standorte" || path.startsWith("/standorte/");
    if (!isDynamic) {
      if (window.__swnDismissOverlay) window.__swnDismissOverlay();
      else overlay.remove();
      return;
    }
    const timer = setInterval(() => {
      if (window.__swnPageReady) {
        (window.__swnDismissOverlay || (() => {}))();
        clearInterval(timer);
      } else if (window.__swnPageError) {
        (window.__swnOverlayNotice || (() => {}))();
        clearInterval(timer);
      }
    }, 50);
    return () => clearInterval(timer);
  }, []);
  return null;
}

function AdminFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0E0E10]" data-testid="admin-loading">
      <p className="text-sm text-[#A1A1AA]">Bereich wird geladen …</p>
    </div>
  );
}

function PublicLayout() {
  return (
    <div className="flex min-h-screen flex-col bg-[#0E0E10]">
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <ConsentBanner />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ConsentProvider>
        <AuthProvider>
          <ScrollManager />
          <PrerenderDismiss />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/standorte" element={<Standorte />} />
              <Route path="/standorte/:slug" element={<StandortDetail />} />
              <Route path="/spielerschutz" element={<Spielerschutz />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route
              path="/admin/login"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <AdminLogin />
                </Suspense>
              }
            />
            <Route
              path="/admin"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <AdminLayout />
                </Suspense>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminLocations />
                  </Suspense>
                }
              />
              <Route
                path="standorte/neu"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminLocationForm />
                  </Suspense>
                }
              />
              <Route
                path="standorte/:id"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminLocationForm />
                  </Suspense>
                }
              />
              <Route
                path="aktionen"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminPromotions />
                  </Suspense>
                }
              />
              <Route
                path="audit"
                element={
                  <Suspense fallback={<AdminFallback />}>
                    <AdminAudit />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
          <Toaster theme="dark" position="top-right" richColors />
        </AuthProvider>
      </ConsentProvider>
    </BrowserRouter>
  );
}

export default App;
